export const DESKTOP_SOCIAL_PAIRING_FLOW = "desktop-social";

export type DesktopSocialProvider = "google" | "microsoft" | "apple";

export interface DesktopPairingAuthAttempt {
  provider: DesktopSocialProvider;
  startedAt: number;
}

const STORAGE_PREFIX = "viewr:desktop-pairing-auth:";

export function parseDesktopSocialProvider(
  value: string | null | undefined,
): DesktopSocialProvider | null {
  const normalized = (value ?? "").trim().toLowerCase();
  if (
    normalized === "google" ||
    normalized === "microsoft" ||
    normalized === "apple"
  ) {
    return normalized;
  }
  return null;
}

export function isDesktopSocialPairingFlow(
  flow: string | null | undefined,
  provider: DesktopSocialProvider | null,
): boolean {
  return flow === DESKTOP_SOCIAL_PAIRING_FLOW && provider !== null;
}

export function isDesktopSocialPairingLocation(
  location: Pick<Location, "pathname" | "search"> = window.location,
): boolean {
  if (location.pathname !== "/pair") return false;

  const params = new URLSearchParams(location.search);
  return Boolean(
    params.get("session") &&
      params.get("code") &&
      isDesktopSocialPairingFlow(
        params.get("flow"),
        parseDesktopSocialProvider(params.get("provider")),
      ),
  );
}

export function desktopPairingAuthAttemptKey(sessionId: string): string {
  return `${STORAGE_PREFIX}${sessionId}`;
}

export function readDesktopPairingAuthAttempt(
  sessionId: string,
): DesktopPairingAuthAttempt | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(
      desktopPairingAuthAttemptKey(sessionId),
    );
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<DesktopPairingAuthAttempt>;
    const provider = parseDesktopSocialProvider(parsed.provider);
    if (!provider || typeof parsed.startedAt !== "number") return null;
    return { provider, startedAt: parsed.startedAt };
  } catch {
    return null;
  }
}

export function writeDesktopPairingAuthAttempt(
  sessionId: string,
  provider: DesktopSocialProvider,
  startedAt: number = Date.now(),
): DesktopPairingAuthAttempt {
  const attempt = { provider, startedAt };
  if (typeof window !== "undefined") {
    window.sessionStorage.setItem(
      desktopPairingAuthAttemptKey(sessionId),
      JSON.stringify(attempt),
    );
  }
  return attempt;
}

export function clearDesktopPairingAuthAttempt(sessionId: string): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(desktopPairingAuthAttemptKey(sessionId));
}
